import React, {useEffect, useState} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import {
  CustomInput,
  CustomSelect,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';

import {ConfirmationProvider} from '../Components/ConfirmationContext';
import {AlertProvider, useAlert} from '../Components/AlertContext';
import {
  ProductCatalogApiProvider,
  useProductCatalogApi,
} from '../Services/ProductCatalogApiProvider';
import {CatalogTaxonomy} from "../Components/CatalogTaxonomy";

function EditProductCatalog({canEdit}) {
  const location = useLocation();
  const {organizationId, catalogId} = useParams();
  const {
    getCatalogById,
    mapCatalog,
    getConnections,
    updateCatalogTaxonomy,
  } = useProductCatalogApi();
  const {showAlert} = useAlert();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);

  const [catalogName, setCatalogName] = useState('Product Catalog');
  const [catalogRawSchema, setCatalogRawSchema] = useState([]);
  const [catalogRawSchemaOptions, setCatalogRawSchemaOptions] = useState([]);
  const [productBaseAddress, setProductBaseAddress] = useState('');
  const [handleKey, setHandleKey] = useState('');
  const [titleKey, setTitleKey] = useState('');
  const [skuKey, setSkuKey] = useState('');
  const [productAttributes, setProductAttributes] = useState([]);
  const [variantAttributes, setVariantAttributes] = useState([]);
  const [productAttributeMapping, setProductAttributeMapping] = useState({});
  const [connections, setConnections] = useState([]);
  const [connectionKey, setConnectionKey] = useState('');

  const [taxonomy, setTaxonomy] = useState({});

  const isAssetCatalog =
    catalog && (catalog.catalog_type === 'ASSET' || catalog.catalog_type === 'asset');

  function onSetKey(key) {
    setProductAttributes((prev) => {
      if (!key || key === '') {
        return [...prev];
      }

      const productKey = prev.filter((item) => item === key);

      if (productKey.length === 0) {
        return [...prev, key];
      } else {
        return [...prev];
      }
    });
  }

  function onSetVariantKey(key) {
    setVariantAttributes((prev) => {
      if (!key || key === '') {
        return [...prev];
      }

      const variantKey = prev.filter((item) => item === key);

      if (variantKey.length === 0) {
        return [...prev, key];
      } else {
        return [...prev];
      }
    });
  }

  function onSetKeys(keys) {
    setProductAttributes((prev) => {
      const validKeys = keys.filter(
        (item) => item !== '' && item !== undefined,
      );

      const newKeys = validKeys.filter((vk) => {
        const productKey = prev.filter((item) => item === vk);
        return productKey.length === 0;
      });

      return [...prev, ...newKeys];
    });
  }

  function onSetVariantKeys(keys) {
    setVariantAttributes((prev) => {
      const validKeys = keys.filter(
        (item) => item !== '' && item !== undefined,
      );

      const newKeys = validKeys.filter((vk) => {
        const variantKey = prev.filter((item) => item === vk);
        return variantKey.length === 0;
      });

      return [...prev, ...newKeys];
    });
  }

  function onSetTitleKey(val) {
    setTitleKey(val);
    onSetKey(val);
  }

  function onSetHandleKey(val) {
    setHandleKey(val);
    onSetKey(val);
  }

  function onSetSkuKey(val) {
    setSkuKey(val);
    onSetVariantKey(val);
  }

  function onSetProductAttributes(vals) {
    setProductAttributes(vals);
    onSetKeys([handleKey, titleKey]);
  }

  function onSetVariantAttributes(vals) {
    setVariantAttributes(vals);
    onSetVariantKeys([skuKey]);
  }

  function onSetProductAttributeMapping(item, val) {
    setProductAttributeMapping((prev) => {
      return {...prev, [item]: val};
    });
  }

  function onUpdateTaxonomy() {
    setLoading(true);
    updateCatalogTaxonomy(catalogId, taxonomy)
    setLoading(false);
    showAlert('success', 'Catalog taxonomy was successfully saved!');
    navigate(`/organizations/${organizationId}/product-catalogs/${catalogId}`, {
      state: {organizationId},
    });
  }

  async function onSave() {
    // === Якщо це ASSET — працюємо як раніше, з мапінгом ===
    if (isAssetCatalog) {
      if (!handleKey || handleKey === '') {
        showAlert('fail', 'Please select product handle column!');
        return;
      }
      if (!titleKey || titleKey === '') {
        showAlert('fail', 'Please select product title column!');
        return;
      }
      if (!skuKey || skuKey === '') {
        showAlert('fail', 'Please select product variant sku column!');
        return;
      }
      if (productAttributes.length < 1) {
        showAlert('fail', 'Please select product attributes columns!');
        return;
      }
      if (variantAttributes.length < 1) {
        showAlert('fail', 'Please select variant attributes columns!');
        return;
      }

      setLoading(true);

      const catalogMapping = {};

      productAttributes.forEach((item) => {
        const mappedItem = productAttributeMapping[item] ?? item;
        catalogMapping[item] = mappedItem;
      });
      variantAttributes.forEach((item) => {
        const mappedItem = productAttributeMapping[item] ?? item;
        catalogMapping[item] = mappedItem;
      });

      mapCatalog(
        catalogId,
        handleKey,
        titleKey,
        skuKey,
        catalogMapping,
        productAttributes,
        variantAttributes,
        productBaseAddress,
        catalogName,
      )
        .then(() => {
          setLoading(false);
          showAlert('success', 'Product catalog was successfully saved!');
          navigate(`/organizations/${organizationId}/product-catalogs`, {
            state: {organizationId},
          });
        })
        .catch(() => {
          setLoading(false);
          showAlert('fail', 'Failed to save product catalog!');
        });

      return;
    }

    // === Якщо це НЕ ASSET (наприклад KEY_CRM) — шлемо через mapCatalog тільки імʼя (інші поля — пусті) ===
    setLoading(true);

    const emptyMapping = {};
    const emptyArray = [];
    const emptyBaseAddress = '';

    mapCatalog(
      catalogId,
      '',                 // handle_key
      '',                 // title_key
      '',                 // sku_key
      emptyMapping,       // attributes
      emptyArray,         // product_keys
      emptyArray,         // variant_keys
      emptyBaseAddress,   // base_address
      catalogName,        // name — ЄДИНЕ, що реально важливе для non-ASSET
    )
      .then(async () => {
        // ← ДОДАНО оновлення таксономії
        try {
          await updateCatalogTaxonomy(catalogId, taxonomy);
        } catch (e) {
          console.error('Failed to update catalog taxonomy', e);
        }

        setLoading(false);
        showAlert('success', 'Product catalog was successfully saved!');
        navigate(`/organizations/${organizationId}/product-catalogs`, {
          state: {organizationId},
        });
      })
      .catch(() => {
        setLoading(false);
        showAlert('fail', 'Failed to save product catalog!');
      });
  }

  useEffect(() => {
    async function fetchCatalog() {
      const fetchedCatalog = await getCatalogById(catalogId);
      setCatalog(fetchedCatalog);
      console.log("fetched catalog", fetchedCatalog);
      setTaxonomy(fetchedCatalog.taxonomy ?? {})

      setCatalogName(fetchedCatalog.name ?? '');
      setCatalogRawSchema(fetchedCatalog.raw_schema || []);
      setCatalogRawSchemaOptions(
        (fetchedCatalog.raw_schema || []).map((schema) => ({
          value: schema,
          label: schema,
        })),
      );
      setProductBaseAddress(fetchedCatalog.base_address ?? '');
      setHandleKey(fetchedCatalog.handle_key ?? '');
      setTitleKey(fetchedCatalog.title_key ?? '');
      setSkuKey(fetchedCatalog.sku_key ?? '');
      setProductAttributes(fetchedCatalog.product_keys ?? []);
      setVariantAttributes(fetchedCatalog.variant_keys ?? []);
      setProductAttributeMapping(fetchedCatalog.mapped_attributes ?? {});
      setConnectionKey(fetchedCatalog.connection_key || '');

      setLoading(false);
    }

    fetchCatalog();
  }, [getCatalogById, catalogId]);

  useEffect(() => {
    getConnections().then((conns) => {
      setConnections(conns ?? []);
    });
  }, [getConnections]);

  if (loading) {
    return <PageLoader/>;
  }

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="form-container radius-yes p-2 bg-yes col-12 gap d-flex flex-column">
          <h2 className="h2-title">{catalogName || 'Product Catalog'}</h2>
          <div
            id="shop-export"
            className="int-col-2 p-2 border-yes radius-yes"
          >
            {!isAssetCatalog && (
              <>
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Catalog Name</div>
                    <div className="small-font">Edit catalog name</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomInput
                      id="catalog-name"
                      label="Catalog Name"
                      value={catalogName}
                      onChange={(id, value) => setCatalogName(value)}
                      placeholder="Enter catalog name"
                      type="text"
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Connection</div>
                    <div className="small-font">Connection is not editable</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="connection-select"
                      label="Connection"
                      options={(connections || []).map(conn => ({
                        value: conn.id,
                        label: conn.name,
                      }))}
                      value={connectionKey}
                      multiSelect={false}
                      onChange={() => {
                      }}
                      disabled={true}
                    />
                  </div>
                </div>
              </>
            )}

            {isAssetCatalog && (
              <>
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Products Base URL</div>
                    <div className="small-font">
                      Insert URL of product page base address
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomInput
                      id="shop-url"
                      label="E-commerce products base URL"
                      value={productBaseAddress}
                      onChange={(id, value) =>
                        setProductBaseAddress(value)
                      }
                      placeholder="http://www.shop.com/products"
                      icon={<i className="fa-solid fa-tags"></i>}
                      defaultValue="http://www.shop.com/products"
                      type="text"
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">
                      Product Handle Column
                    </div>
                    <div className="small-font">
                      Select product handle column
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="handle-select"
                      label="Product Handle"
                      options={catalogRawSchemaOptions}
                      value={handleKey}
                      multiSelect={false}
                      onChange={(val) => onSetHandleKey(val)}
                      key={handleKey}
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">
                      Product Title Column
                    </div>
                    <div className="small-font">
                      Select product title column
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="title-select"
                      label="Product Title"
                      options={catalogRawSchemaOptions}
                      value={titleKey}
                      multiSelect={false}
                      onChange={(val) => onSetTitleKey(val)}
                      key={titleKey}
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">
                      Product Attributes Columns
                    </div>
                    <div className="small-font">
                      Select product attributes columns
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="attributes-select"
                      label="Product Attributes"
                      options={catalogRawSchemaOptions}
                      value={productAttributes}
                      multiSelect={true}
                      onChange={(val) =>
                        onSetProductAttributes(val)
                      }
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">
                      Variant SKU Column
                    </div>
                    <div className="small-font">
                      Select variant sku column
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="sku-select"
                      label="Product Variant SKU"
                      options={catalogRawSchemaOptions}
                      value={skuKey}
                      multiSelect={false}
                      onChange={(val) => onSetSkuKey(val)}
                      key={skuKey}
                    />
                  </div>
                </div>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">
                      Variant Attributes Columns
                    </div>
                    <div className="small-font">
                      Select variant attributes columns
                    </div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="attributes-select"
                      label="Variant Attributes"
                      options={catalogRawSchemaOptions}
                      value={variantAttributes}
                      multiSelect={true}
                      onChange={(val) =>
                        onSetVariantAttributes(val)
                      }
                    />
                  </div>
                </div>

                {productAttributes &&
                  productAttributes.map((item, index) => (
                    <div
                      key={`attribute-mapping-block-${index}`}
                      className="input-block d-flex flex-row justify-between"
                    >
                      <div className="input-description">
                        <div className="input-title">{item}</div>
                        <div className="small-font"></div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomInput
                          id={`attribute-mapping-${index}`}
                          label=""
                          placeholder=""
                          value={
                            productAttributeMapping[item] ?? item
                          }
                          onChange={(id, value) =>
                            onSetProductAttributeMapping(
                              item,
                              value,
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}

                {variantAttributes &&
                  variantAttributes.map((item, index) => (
                    <div
                      key={`attribute-variant-mapping-block-${index}`}
                      className="input-block d-flex flex-row justify-between"
                    >
                      <div className="input-description">
                        <div className="input-title">{item}</div>
                        <div className="small-font"></div>
                      </div>
                      <div className="align-wrapper d-flex align-center">
                        <CustomInput
                          id={`attribute-variant-mapping-${index}`}
                          label=""
                          placeholder=""
                          value={
                            productAttributeMapping[item] ?? item
                          }
                          onChange={(id, value) =>
                            onSetProductAttributeMapping(
                              item,
                              value,
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
              </>
            )}

            <Button
              variant="primary"
              size="medium"
              onClick={() =>
                navigate(
                  `/organizations/${organizationId}/product-catalogs`,
                )
              }
            >
              Cancel
            </Button>
            <Button variant="primary" size="medium" onClick={onSave}>
              Save
            </Button>
          </div>
        </div>
      </div>
      <div className="row mt-4">
        <div className="radius-yes p-2 bg-yes">
          <h3 className="h3-title">Catalog taxonomy</h3>

          <CatalogTaxonomy
            taxonomyCatalog={taxonomy}
            canEdit={canEdit}
            onChange={(updated) => {
              setTaxonomy(updated);
            }}
            onSaveTaxonomy={onUpdateTaxonomy}
          />
        </div>
      </div>

    </div>
  );
}

function EditProductCatalogWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ProductCatalogApiProvider>
          <EditProductCatalog canEdit={canEdit}/>
        </ProductCatalogApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default EditProductCatalogWrapper;
